/* eslint-disable max-lines */
import { Fragment, NodeType, Schema, Slice } from 'prosemirror-model';
import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { EditorState, TextSelection, Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

export type ColumnValue = number;

export interface OriginalCursorInfo {
  colIndex: number;
  offset: number;
}

const METADATA_UNWRAP_POSITION = 'unwrapPosition';

export class ColumnsService {
  constructor(
    private readonly columnsNodeType: NodeType,
    private readonly columnNodeType: NodeType,
    private readonly paragraphNodeType: NodeType,
    private readonly schema: Schema,
  ) {}

  public getColumnsCount(state: EditorState): ColumnValue {
    const $from = state.doc.resolve(state.selection.from);
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.type.name === 'columns') {
        const count = node.childCount;
        if (count >= 1 && count <= 4) {
          return <ColumnValue>count;
        }
      }
    }
    return <ColumnValue>1;
  }

  /**
   * Main setting columns method.
   * It performs three transactions:
   *   1. Transform the document (create/update the columns node).
   *   2. (If creating new columns) Insert an extra paragraph after the columns node.
   *   3. Compute and set the final selection (cursor).
   */
  public setColumns(targetCount: number, state: EditorState, editorView: EditorView): void {
    const originalCursor = this.getOriginalCursorInfo(state);
    const expandedRange = this.getExpandedRange(state);
    const originalParent = this.findOutermostColumnsNode(state, state.selection.from);
    const updatingExisting = !!originalParent;
    const unwrapping = targetCount === 1 && updatingExisting;

    // Build document transformation
    let tr1 = this.transformDocumentForColumns(targetCount, state);

    // Insert extra paragraph only if creating new columns (not unwrapping)
    if (!unwrapping && !updatingExisting && targetCount !== 1) {
      const expandedFrom = this.getExpandedRange(state).from;

      const parentColumns = this.findFirstColumnsNodeAfterPos(tr1.doc, expandedFrom);
      if (parentColumns) {
        const posAfter = parentColumns.pos + parentColumns.node.nodeSize - 1;

        const para = this.paragraphNodeType.create();
        tr1 = tr1.insert(posAfter, para);
      }
    }

    editorView.dispatch(tr1);

    // Compute final cursor position and update selection
    const newState = editorView.state;
    let finalPos;

    if (unwrapping) {
      // For unwrapping, use the stored unwrap position or default to a sensible position
      const unwrapPos = tr1.getMeta(METADATA_UNWRAP_POSITION);
      if (unwrapPos !== undefined) {
        finalPos = unwrapPos;

        // Try to find a text node near this position
        const $pos = newState.doc.resolve(finalPos);
        if ($pos.nodeAfter && $pos.nodeAfter.isTextblock) {
          finalPos += 1; // Move into text node
        }
      } else {
        finalPos = expandedRange.from;
      }
    } else if (targetCount !== 1) {
      // For multi-column layouts
      finalPos = this.computeFinalCursorPosition(newState, originalCursor, expandedRange.from);
    } else {
      // Default case
      finalPos = state.selection.from;
    }

    // Ensure final position is valid
    finalPos = Math.max(0, Math.min(finalPos, newState.doc.content.size));

    // Set selection and dispatch
    const trFinal = newState.tr.setSelection(TextSelection.near(newState.doc.resolve(finalPos)));
    editorView.dispatch(trFinal);
  }

  public getColumnStart(
    columnsNode: ProsemirrorNode,
    parentPos: number,
    targetIndex: number,
  ): number {
    let pos = parentPos + 1;
    for (let i = 0; i < targetIndex; i++) {
      pos += columnsNode.child(i).nodeSize;
    }
    return pos + 1;
  }

  private computeFinalCursorPosition(
    state: EditorState,
    originalCursor: OriginalCursorInfo,
    expandedFrom: number,
  ): number {
    // Find the columns node at or near expandedFrom
    const parentColumns = this.findOutermostColumnsNode(state, expandedFrom);
    if (!parentColumns) {
      return expandedFrom;
    }

    // Get target column index, defaulting to the first if original is out of bounds
    const targetColIndex = Math.min(originalCursor.colIndex, parentColumns.node.childCount - 1);

    // Calculate position in the target column
    const colStart = this.getColumnStart(parentColumns.node, parentColumns.pos, targetColIndex);
    const targetColumn = parentColumns.node.child(targetColIndex);

    if (targetColumn && targetColumn.childCount > 0) {
      // Position cursor in the first paragraph of the target column
      const firstChild = targetColumn.child(0);
      const contentLength = firstChild.content.size;
      const finalOffset = Math.min(originalCursor.offset, contentLength);
      return colStart + finalOffset;
    }

    // If no content in target column, position at its start
    return colStart;
  }

  /**
   * Returns the expanded range (from and to) for the current selection.
   * If the selection is entirely within a node that should be preserved – for lists,
   * for headings (if fully selected), or paragraphs etc. – returns that node's full range.
   */
  private getExpandedRange(state: EditorState): { from: number; to: number } {
    const { from, to } = state.selection;
    const $from = state.doc.resolve(from);
    const $to = state.doc.resolve(to);

    if (from === 0 && to === state.doc.content.size) {
      return { from, to };
    }

    let expandFrom = from;
    let expandTo = to;

    // Expand selection to full block nodes if inside paragraphs, lists, blockquotes
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (depth === 1 || ['bullet_list', 'ordered_list', 'blockquote'].includes(node.type.name)) {
        expandFrom = $from.start(depth);
        break;
      }
    }

    for (let depth = $to.depth; depth > 0; depth--) {
      const node = $to.node(depth);
      if (depth === 1 || ['bullet_list', 'ordered_list', 'blockquote'].includes(node.type.name)) {
        expandTo = $to.end(depth);
        break;
      }
    }

    if ($from.depth >= 1 && $from.node(1)?.isBlock) {
      const blockStart = $from.start(1);
      const blockEnd = $from.end(1);

      let pos = blockStart;
      while (pos < blockEnd) {
        const result = state.doc.childAfter(pos);
        const node = result.node;
        const offset = result.offset;

        if (!node) break;

        if (offset <= expandFrom && offset + node.nodeSize > expandFrom) {
          expandFrom = offset;
        }

        if (offset < expandTo && offset + node.nodeSize >= expandTo) {
          expandTo = offset + node.nodeSize;
        }

        pos = offset + node.nodeSize;
      }
    }

    return { from: expandFrom, to: expandTo };
  }

  /**
   * Applies the columns' transformation.
   * If an outermost columns node exists at the selection, updates it.
   * Otherwise, creates a new columns node preserving content in the first column.
   */
  private transformDocumentForColumns(targetCount: number, state: EditorState): Transaction {
    let tr: Transaction = state.tr;
    const { from: selFrom } = state.selection;
    const { from: expandedFrom, to: expandedTo } = this.getExpandedRange(state);
    const $from = state.doc.resolve(selFrom);

    const parentColumns = this.findOutermostColumnsNode(state, selFrom);
    if (parentColumns) {
      tr = this.updateExistingColumns(state, tr, targetCount, parentColumns);
      return tr;
    }

    const columnNodes: ProsemirrorNode[] = [];

    // Case: creating columns from empty paragraph
    if (state.selection.empty && $from.parent.content.size === 0) {
      for (let i = 0; i < targetCount; i++) {
        columnNodes.push(this.columnNodeType.create({}, this.paragraphNodeType.create()));
      }
      const columnsNode = this.columnsNodeType.create({}, columnNodes);
      tr = tr.replaceSelectionWith(columnsNode);
      return tr;
    }

    // Otherwise, collect top-level nodes between expandedFrom and expandedTo
    const nodes: ProsemirrorNode[] = [];
    let pos = expandedFrom;

    // Collect all nodes in the expanded range
    while (pos < expandedTo) {
      const { node, offset } = state.doc.childAfter(pos);
      if (!node) break;

      nodes.push(node);
      pos = offset + node.nodeSize;
    }

    const firstColContent = Fragment.fromArray(
      nodes.length > 0 ? nodes : [this.paragraphNodeType.create()],
    );

    // Build columns
    columnNodes.push(this.columnNodeType.create({}, firstColContent));
    for (let i = 1; i < targetCount; i++) {
      columnNodes.push(this.columnNodeType.create({}, this.paragraphNodeType.create()));
    }

    const columnsNode = this.columnsNodeType.create({}, columnNodes);
    const slice = new Slice(Fragment.from(columnsNode), 0, 0);

    // Replace full expanded range
    tr = tr.replaceRange(expandedFrom, expandedTo, slice);
    return tr;
  }

  private insertExtraParagraphIfNeeded(state: EditorState): Transaction {
    const expandedFrom = this.getExpandedRange(state).from;
    const parentColumns = this.findOutermostColumnsNode(state, expandedFrom);
    if (parentColumns) {
      const posAfter = parentColumns.pos + parentColumns.node.nodeSize - 1;
      return state.tr.insert(posAfter, this.paragraphNodeType.create());
    }
    return state.tr;
  }

  private updateExistingColumns(
    state: EditorState,
    _tr: Transaction,
    targetCount: number,
    parentInfo: { pos: number; node: ProsemirrorNode },
  ): Transaction {
    const currentCount = parentInfo.node.childCount;
    const newColumnNodes: ProsemirrorNode[] = [];
    let tr: Transaction = _tr;

    if (targetCount === 1) {
      // Unwrapping columns completely
      const extractedNodes: ProsemirrorNode[] = [];

      // Gather all content from all columns
      for (let i = 0; i < currentCount; i++) {
        const column = parentInfo.node.child(i);
        for (let j = 0; j < column.childCount; j++) {
          extractedNodes.push(column.child(j));
        }
      }

      // If no content was found, add an empty paragraph
      if (extractedNodes.length === 0) {
        extractedNodes.push(this.paragraphNodeType.create());
      }

      // Create content for replacement
      const replacementContent = Fragment.fromArray(extractedNodes);

      // Calculate the start and end positions for replacement
      const startPos = parentInfo.pos;
      const endPos = startPos + parentInfo.node.nodeSize;

      // Replace the columns node with the extracted content
      tr = tr.replaceWith(startPos, endPos, replacementContent);

      // Store a good cursor position for later use
      tr = tr.setMeta(METADATA_UNWRAP_POSITION, startPos);

      return tr;
    } else if (targetCount > currentCount) {
      // Adding more columns
      for (let i = 0; i < currentCount; i++) {
        newColumnNodes.push(parentInfo.node.child(i));
      }

      // Add new empty columns
      for (let i = currentCount; i < targetCount; i++) {
        newColumnNodes.push(this.columnNodeType.create({}, this.paragraphNodeType.create()));
      }

      // Create and replace columns node
      const newColumnsNode = this.columnsNodeType.create({}, newColumnNodes);
      tr = tr.replaceWith(
        parentInfo.pos,
        parentInfo.pos + parentInfo.node.nodeSize,
        newColumnsNode,
      );

      return tr;
    } else {
      // Reducing number of columns
      // Keep content from columns we're keeping
      for (let i = 0; i < targetCount - 1; i++) {
        newColumnNodes.push(parentInfo.node.child(i));
      }

      // Merge all remaining columns into the last one
      const lastColumnIndex = targetCount - 1;
      let mergedContent = parentInfo.node.child(lastColumnIndex).content;

      // Add content from extra columns that will be removed
      for (let i = targetCount; i < currentCount; i++) {
        const extraCol = parentInfo.node.child(i);
        if (extraCol.childCount > 0) {
          mergedContent = mergedContent.append(extraCol.content);
        }
      }

      // Create the merged last column
      newColumnNodes.push(this.columnNodeType.create({}, mergedContent));

      // Create and replace columns node
      const newColumnsNode = this.columnsNodeType.create({}, newColumnNodes);
      tr = tr.replaceWith(
        parentInfo.pos,
        parentInfo.pos + parentInfo.node.nodeSize,
        newColumnsNode,
      );

      return tr;
    }
  }

  private getOriginalCursorInfo(state: EditorState): OriginalCursorInfo {
    const { from: selFrom } = state.selection;
    const parentColumns = this.findOutermostColumnsNode(state, selFrom);
    if (parentColumns) {
      let sum = 0;
      let colIndex = 0;
      for (let i = 0; i < parentColumns.node.childCount; i++) {
        const colSize = parentColumns.node.child(i).nodeSize;
        if (selFrom - parentColumns.pos < sum + colSize) {
          colIndex = i;
          break;
        }
        sum += colSize;
      }
      const offset = selFrom - (parentColumns.pos + sum);
      return { colIndex, offset };
    }
    const $from = state.doc.resolve(selFrom);
    const expandedFrom = $from.parent.type === this.paragraphNodeType ? $from.start() : selFrom;
    return { colIndex: 0, offset: selFrom - expandedFrom };
  }

  private findOutermostColumnsNode(
    state: EditorState,
    pos: number,
  ): { pos: number; node: ProsemirrorNode; depth: number } | null {
    const $pos = state.doc.resolve(pos);
    let result: { pos: number; node: ProsemirrorNode; depth: number } | null = null;

    // Start searching from depth 1 to find the outermost columns node
    for (let depth = 1; depth <= $pos.depth; depth++) {
      const node = $pos.node(depth);
      if (node.type === this.columnsNodeType) {
        if (!result || depth < result.depth) {
          result = {
            pos: $pos.before(depth),
            node,
            depth,
          };
        }
      }
    }

    return result;
  }

  private findFirstColumnsNodeAfterPos(
    doc: ProsemirrorNode,
    from: number,
  ): { pos: number; node: ProsemirrorNode } | null {
    let pos = from;
    while (pos < doc.content.size) {
      const { node, offset } = doc.childAfter(pos);
      if (!node) break;
      if (node.type === this.columnsNodeType) {
        return { pos, node };
      }
      pos = offset + node.nodeSize;
    }
    return null;
  }
}
